import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Crosshair, LocateFixed } from "lucide-react";
import { TspBreadcrumb } from "../../../components/primitives";
import TspLoadError from "../../../components/common/TspLoadError";
import { JOURNEY_STATUS_LABELS, JOURNEY_STATUS_PILL } from "../../../constants/tspJourneyStatus";
import useTspJourneyTracking from "./useTspJourneyTracking";
import JourneyGpsControl from "./JourneyGpsControl";
import TspLiveLocationMap from "./TspLiveLocationMap";
import { HEALTH_UI, STOP_REASON_TEXT, formatAccuracy, timeAgo, userColor } from "./trackingModel";

// "View Current Live Location": the journey's live map — every tracker's
// current / last known position and the route they have moved along, kept
// current by polling (useTspJourneyTracking). This device's own GPS toggle is
// here too, so a leader can switch tracking on from the map.
export default function TspJourneyLiveLocationPage() {
  const { journeyId } = useParams();
  const { status, error, lines, pathLoaded, refresh } = useTspJourneyTracking(journeyId, { statusMs: 5000 });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [follow, setFollow] = useState(true);
  const [fitKey, setFitKey] = useState(0);

  const trackers = useMemo(() => status?.trackers || [], [status]);
  const userIds = useMemo(() => {
    const ids = trackers.map((tracker) => String(tracker.user.id));
    lines.forEach((line) => !ids.includes(String(line.userId)) && ids.push(String(line.userId)));
    return ids.sort();
  }, [trackers, lines]);
  const userIdsKey = userIds.join(",");
  // Stable per set of users, so the map does not redraw on every poll.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorFor = useCallback((userId) => userColor(userIds, userId), [userIdsKey]);

  // Default selection: me if I am tracking, else the first live tracker, else anyone with a position.
  useEffect(() => {
    if (selectedUserId || !trackers.length) return;
    const pick = trackers.find((t) => t.is_me && t.last_fix) || trackers.find((t) => t.health === "LIVE") || trackers.find((t) => t.last_fix);
    if (pick) setSelectedUserId(String(pick.user.id));
  }, [trackers, selectedUserId]);

  // Fit the whole route once everything has loaded.
  useEffect(() => {
    if (pathLoaded && status && fitKey === 0) setFitKey(1);
  }, [pathLoaded, status, fitKey]);

  if (error && !status) return <TspLoadError error={error} onRetry={refresh} what="this journey's live location" />;
  if (!status) return <div className="tsp-page tsp-dashboard-loading">Loading live location…</div>;

  const { journey } = status;
  const liveCount = trackers.filter((tracker) => tracker.health === "LIVE").length;
  const hasAnything = trackers.some((tracker) => tracker.last_fix) || lines.size > 0;

  const select = (userId) => {
    setSelectedUserId(String(userId));
    setFollow(true);
  };

  return (
    <div className="tsp-page tsp-live-page">
      <TspBreadcrumb items={[{ label: "Dashboard", to: "/tsp" }, { label: "Journeys", to: "/tsp/journeys" }, { label: journey.journey_name, to: `/tsp/journeys/${journey.id}` }, "Live Location"]} />

      <div className="tsp-live-header">
        <div className="tsp-live-title">
          <h1>Live Location</h1>
          <p>
            {journey.journey_name}
            <span className={`tsp-status-pill ${JOURNEY_STATUS_PILL[journey.status]}`}>{JOURNEY_STATUS_LABELS[journey.status]}</span>
          </p>
        </div>
        <Link to={`/tsp/journeys/${journey.id}`} className="tsp-btn tsp-btn-outline">
          <ArrowLeft size={15} aria-hidden="true" /> Back to Journey
        </Link>
      </div>

      <section className="tsp-card tsp-gps-bar">
        <JourneyGpsControl journeyId={journey.id} status={status} onChange={refresh} compact />
        {error && <span className="tsp-gps-note tsp-live-poll-error">Could not refresh — showing the last known data. Retrying…</span>}
      </section>

      <div className="tsp-live-layout">
        <div className="tsp-card tsp-live-map-card">
          <TspLiveLocationMap
            lines={lines}
            trackers={trackers}
            colorFor={colorFor}
            selectedUserId={selectedUserId}
            follow={follow}
            onUserMove={() => setFollow(false)}
            fitKey={fitKey}
          />
          <div className="tsp-live-map-tools">
            <button type="button" className={`tsp-btn ${follow ? "tsp-btn-success" : "tsp-btn-outline"}`} onClick={() => setFollow((value) => !value)} disabled={!selectedUserId} aria-pressed={follow}>
              <LocateFixed size={15} aria-hidden="true" /> {follow ? "Following" : "Follow"}
            </button>
            <button type="button" className="tsp-btn tsp-btn-outline" onClick={() => { setFollow(false); setFitKey((key) => key + 1); }} disabled={!hasAnything}>
              <Crosshair size={15} aria-hidden="true" /> Show whole route
            </button>
          </div>
          {!hasAnything && (
            <div className="tsp-live-empty">
              {journey.trackable
                ? "No location has been shared for this journey yet. Press Enable GPS to start sharing this device's location."
                : "No location was shared for this journey. Live tracking is available while a journey is in progress."}
            </div>
          )}
        </div>

        <aside className="tsp-card tsp-live-side" aria-label="People on the map">
          <h2 className="tsp-section-nav-title">
            On the map <span className="tsp-live-count">{liveCount} live</span>
          </h2>
          {trackers.length === 0 ? (
            <p className="tsp-muted-text">Nobody has shared a location yet.</p>
          ) : (
            <ul className="tsp-live-people">
              {trackers.map((tracker) => {
                const health = HEALTH_UI[tracker.health] || HEALTH_UI.OFF;
                const id = String(tracker.user.id);
                const accuracy = formatAccuracy(tracker.last_fix?.accuracy);
                return (
                  <li key={id}>
                    <button type="button" className={`tsp-live-person${id === selectedUserId ? " is-selected" : ""}`} onClick={() => select(id)} disabled={!tracker.last_fix} aria-pressed={id === selectedUserId}>
                      <span className="tsp-live-person-color" style={{ background: colorFor(id) }} aria-hidden="true" />
                      <span className="tsp-live-person-body">
                        <strong>
                          {tracker.user.name || "Unknown"}
                          {tracker.is_me ? " (you)" : ""}
                        </strong>
                        <span className={`tsp-gps-badge tone-${health.tone}`}>
                          <span className="tsp-gps-dot" aria-hidden="true" />
                          {health.label}
                          {tracker.health === "OFF" && tracker.stop_reason ? ` · ${STOP_REASON_TEXT[tracker.stop_reason] || ""}` : ""}
                        </span>
                        <span className="tsp-live-person-meta">
                          {tracker.last_fix ? `Last position ${timeAgo(tracker.last_fix.t)}` : "No position yet"}
                          {accuracy ? ` · ${accuracy}` : ""}
                          {tracker.status === "ACTIVE" && tracker.health !== "LIVE" ? ` · last report ${timeAgo(new Date(tracker.last_seen_at).getTime())}` : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="tsp-live-legend">
            <span className="tsp-live-legend-start" aria-hidden="true" /> Tracking started · <span className="tsp-live-legend-line" aria-hidden="true" /> Route travelled
          </p>
        </aside>
      </div>
    </div>
  );
}
